import { z } from 'zod';
import { TypedApiClient } from '../index';
import { schemas } from '../../generated/api-client';

// Daily stats: reuse the generated response schema (passthrough — carries all
// view columns; `date` is an ISO string the TypedApiClient converts to TimestampLike).
const { DailyStatsResponseDto } = schemas;
export const DailyStatsResponseSchema = DailyStatsResponseDto;
export type DailyStatsResponse = z.infer<typeof DailyStatsResponseSchema>;

/**
 * Product stats response: a nested dictionary keyed by productId then by date
 * string, e.g. { [productId]: { [date]: { sold, purchased, writtenOff, returned } } }.
 * The date strings are object KEYS (not a `date` field), so the client's date
 * deserialization leaves them untouched — the frontend converts them itself.
 * Entry fields are kept optional + passthrough to avoid validation failures on
 * shape drift (the endpoint is declared as an open object on the backend).
 */
const ProductStatEntrySchema = z
  .object({
    sold: z.number().optional(),
    purchased: z.number().optional(),
    writtenOff: z.number().optional(),
    returned: z.number().optional(),
  })
  .passthrough();
export const ProductStatsResponseSchema = z.record(
  z.string(),
  z.record(z.string(), ProductStatEntrySchema),
);
export type ProductStatsResponse = z.infer<typeof ProductStatsResponseSchema>;

/**
 * Product report response: an array of FLAT legacy per-date docs
 * `{ ...productsKeyedById, date, store }` mirroring the Firestore
 * dailyProductStats productReport docs. Keys are dynamic product ids
 * (plus 'customProduct<name>' / 'delivery' pseudo rows), so the schema is
 * hand-written as an open record — the generated schema for the endpoint is
 * an empty object (additionalProperties only) and would strip everything.
 * Note: the client's date deserialization converts the top-level `date`
 * field ('YYYY-MM-DD') to a TimestampLike, matching the Firestore branch.
 */
export const ProductReportResponseSchema = z.array(z.record(z.unknown()));
export type ProductReportResponse = z.infer<typeof ProductReportResponseSchema>;

// API factory function
export function createReportsApi(client: TypedApiClient) {
  return {
    getDailyStats: (params?: {
      storeId?: string;
      startDate?: string;
      endDate?: string;
    }): Promise<DailyStatsResponse[]> => {
      return client.get('/reports/daily-stats', {
        params: params as Record<string, unknown>,
        responseSchema: z.array(DailyStatsResponseSchema),
      });
    },

    getProductStats: (params: {
      startDate: string;
      endDate: string;
      storeId?: string;
    }): Promise<ProductStatsResponse> => {
      return client.get('/reports/product-stats', {
        params: params as Record<string, unknown>,
        responseSchema: ProductStatsResponseSchema,
      });
    },

    getProductReport: (params: {
      startDate: string;
      endDate: string;
      storeId?: string;
    }): Promise<ProductReportResponse> => {
      return client.get('/reports/product-report', {
        params: params as Record<string, unknown>,
        responseSchema: ProductReportResponseSchema,
      });
    },
  };
}
